// İSG Portal — personel çekmecesi ÇERÇEVESİ (GL5). Yalnız başlık/etiket/özet/belge yuvaları;
// içerik her portalın kendi bileşenidir. Adlı kişide iletişim açıklama olur, adsızda zaten başlıktır.
// Başlık ve açıklama herhangi bir yerden kırılır: adsız kişinin uzun e-postası ✕'in altına taşmaz.
//
// **Düzenleme kipi (#29).** `editor` çekmecenin SAHİBİNDE kurulur (`usePersonnelEditor`) ve buraya
// geçer; `editor.isEditing(personUuid)` iken gövde yalnız formdur (etiket/özet/belge yok → belge
// sorgusu yok), Sheet `tile` + `busy` + footer alır, ✕/Esc/dış tıklama vazgeç korumasından geçer.
// Başlık kişinin adı kalır, açıklama "Personeli düzenle" olur. Çekmece dışarıdan kapanırsa düzenleme
// biter (`leave`) → yeniden açılış asla düzenleme kipinde başlamaz. Bu yüzden sahip çekmeceyi KOŞULSUZ
// render eder (`open` ile; dört çağıranın bugünkü hâli) — koşullu mount'ta kapanış `leave`'i tetiklemez.
// `editor` yoksa görünüm aynıdır.
// Etiket satırı: etiketler sarılır, `actions` (Düzenle) sağda sabit; `notice` hemen altında.

import { type ReactNode, type RefObject, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Sheet } from '../ui/Sheet';
import { type PersonLabelInput, personLabel } from './personLabel';
import type { PersonnelEditor } from './usePersonnelEditor';

export interface PersonnelSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  person: PersonLabelInput | null;
  tags?: ReactNode;
  summary?: ReactNode;
  documents: ReactNode;
  restoreFocusRef?: RefObject<HTMLElement | null>;
  onEscapeKeyDown?: (event: KeyboardEvent) => void;
  /** Düzenleme oturumu — çekmecenin sahibi `usePersonnelEditor` ile kurar. */
  editor?: PersonnelEditor;
  /** Gösterilen kişinin `SupplierUser.Uuid`'i; düzenleme kipi buna göre seçilir. */
  personUuid?: string;
  /** Etiket satırının sağındaki eylem (ör. `PersonnelEditButton`). */
  actions?: ReactNode;
  /** Etiket satırının hemen altı (ör. `editor.notice(uuid)`). */
  notice?: ReactNode;
}

const WRAP = 'block min-w-0 break-words [overflow-wrap:anywhere]';

export function PersonnelSheet({
  open,
  onOpenChange,
  person,
  tags,
  summary,
  documents,
  restoreFocusRef,
  onEscapeKeyDown,
  editor,
  personUuid,
  actions,
  notice,
}: PersonnelSheetProps) {
  const { t } = useTranslation();
  const editing = editor?.isEditing(personUuid ?? null) ? editor : null;

  const leave = editor?.leave;
  useEffect(() => {
    if (!open) {
      leave?.();
    }
  }, [open, leave]);

  const named =
    person !== null &&
    personLabel({ firstName: person.firstName, lastName: person.lastName }) !== '';
  const contact = named ? person?.emailOrPhone : null;
  return (
    <>
      <Sheet
        open={open}
        onOpenChange={(next) => {
          if (!next && editing) {
            editing.requestCloseDrawer();
            return;
          }
          onOpenChange(next);
        }}
        title={<span className={WRAP}>{person ? personLabel(person) : ''}</span>}
        description={
          editing ? (
            t('personnelForm.edit.title')
          ) : contact ? (
            <span className={WRAP}>{contact}</span>
          ) : undefined
        }
        closeLabel={t('common.close')}
        restoreFocusRef={restoreFocusRef}
        onEscapeKeyDown={onEscapeKeyDown}
        tile={editing !== null}
        busy={editing?.busy ?? false}
        footer={editing?.footer}
      >
        {editing ? (
          editing.body
        ) : person ? (
          <div className="flex flex-col gap-5">
            {tags || actions ? (
              <div className="flex items-start justify-between gap-3">
                <div className="flex min-w-0 flex-wrap items-center gap-2">{tags}</div>
                {actions ? <div className="shrink-0">{actions}</div> : null}
              </div>
            ) : null}
            {notice}
            {summary}
            {documents}
          </div>
        ) : null}
      </Sheet>
      {editor?.discardDialogs}
    </>
  );
}
